import React from 'react';
import styled from 'styled-components';
import Button from 'components/atoms/Button';
import Input from 'components/atoms/Input';
import Text from 'components/atoms/Text';
import { COLOR_SYSTEM } from 'components/design/design-system';
import { PREMIUM_ROLES } from 'common/constants/roles';

interface PremiumLicenseFormProps {
  /** 현재 선택된 권한명 */
  selectedRole: string;
  /** 면허번호 입력값 (줄바꿈으로 구분) */
  licenseNumbers: string;
  /** 권한명 변경 핸들러 */
  onRoleChange: (role: string) => void;
  /** 면허번호 입력 변경 핸들러 */
  onLicenseNumbersChange: (value: string) => void;
  /** 추가 버튼 클릭 핸들러 */
  onAdd: () => void;
  /** 삭제 버튼 클릭 핸들러 */
  onDelete: () => void;
  /** 로딩 상태 */
  isLoading?: boolean;
}

const PremiumLicenseForm: React.FC<PremiumLicenseFormProps> = ({
  selectedRole,
  licenseNumbers,
  onRoleChange,
  onLicenseNumbersChange,
  onAdd,
  onDelete,
  isLoading = false,
}) => {
  return (
    <FormContainer>
      <Section>
        <SectionTitle>
          <Text textType="H3" color={COLOR_SYSTEM.get('Gray')?.[900]}>
            권한 선택
          </Text>
        </SectionTitle>

        <RoleSelector>
          <Text textType="Body2" color={COLOR_SYSTEM.get('Gray')?.[700]}>
            Preset 권한
          </Text>
          <PresetButtonGroup>
            {PREMIUM_ROLES.map((role) => (
              <Button
                key={role}
                size="Medium"
                type="Text"
                variant={selectedRole === role ? 'Contained' : 'Outlined'}
                color="Primary"
                onClick={() => onRoleChange(role)}
                disabled={isLoading}
              >
                {role}
              </Button>
            ))}
          </PresetButtonGroup>
        </RoleSelector>

        <RoleInputWrapper>
          <Input
            label="권한명 직접 입력"
            placeholder="예: 서울시약사회/구로구"
            value={selectedRole}
            onChange={(e) => onRoleChange(e.target.value)}
            disabled={isLoading}
            hint="조직명/지역명 형식으로 입력하거나 위 Preset 중 선택"
          />
        </RoleInputWrapper>
      </Section>

      <Section>
        <SectionTitle>
          <Text textType="H3" color={COLOR_SYSTEM.get('Gray')?.[900]}>
            면허번호 입력
          </Text>
        </SectionTitle>

        <Input
          multiLine
          placeholder="면허번호를 한 줄에 하나씩 입력하세요&#10;예:&#10;12345&#10;67890&#10;11223"
          value={licenseNumbers}
          onChange={(e) => onLicenseNumbersChange(e.target.value)}
          disabled={isLoading}
          hint="여러 면허번호를 줄바꿈으로 구분하여 입력"
        />
      </Section>

      <ButtonGroup>
        <Button
          size="Large"
          type="IconWithText"
          variant="Contained"
          color="Primary"
          icon="Plus"
          left
          onClick={onAdd}
          disabled={isLoading || !selectedRole || !licenseNumbers.trim()}
        >
          권한 추가
        </Button>
        <Button
          size="Large"
          type="IconWithText"
          variant="Outlined"
          color="Negative"
          icon="Trash"
          left
          onClick={onDelete}
          disabled={isLoading || !selectedRole || !licenseNumbers.trim()}
        >
          권한 삭제
        </Button>
      </ButtonGroup>
    </FormContainer>
  );
};

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTitle = styled.div`
  margin-bottom: 8px;
`;

const RoleSelector = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PresetButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const RoleInputWrapper = styled.div`
  margin-top: 12px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-start;
  margin-top: 8px;
`;

export default PremiumLicenseForm;
